export const INVALID_SESSION_EVENT = "auth:invalid-session";
export const SESSION_EXPIRED_REASON = "session-expired";
export const AUTH_ROUTE_PATHS = Object.freeze([
  "/signup",
  "/login",
  "/forgot-password",
  "/reset-password",
]);

export function isAuthRoutePath(pathname) {
  const normalizedPath = String(pathname || "/").replace(/\/+$/, "") || "/";
  return AUTH_ROUTE_PATHS.includes(normalizedPath);
}

export function isAuthenticationFailure({ graphQLErrors = [], statusCode = 0 } = {}) {
  if (Number(statusCode) === 401) return true;

  return graphQLErrors.some((error) => {
    const message = String(error?.message || "").toLowerCase();
    const code = String(error?.extensions?.code || "").toUpperCase();
    const layer = String(error?.extensions?.layer || "").toUpperCase();
    return (
      code === "UNAUTHENTICATED" ||
      (code === "FORBIDDEN" && layer === "AUTHENTICATION") ||
      message.includes("not authenticated") ||
      message.includes("authentication required") ||
      message.includes("invalid or expired token")
    );
  });
}

function decodeBase64Url(value) {
  const normalized = String(value || "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const padding = normalized.length % 4;
  const padded = padding ? normalized.padEnd(normalized.length + (4 - padding), "=") : normalized;
  return atob(padded);
}

export function parseJwtPayload(token) {
  if (!token || typeof token !== "string") return null;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(decodeBase64Url(parts[1]));
  } catch {
    return null;
  }
}

export function isJwtExpired(token) {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return true;

  const nowInSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowInSeconds;
}

export function getStoredToken() {
  const token = localStorage.getItem("token");
  return token && typeof token === "string" ? token.trim() || null : null;
}

export function getStoredUser() {
  const stored = localStorage.getItem("user");
  if (!stored) return null;

  try {
    return JSON.parse(stored);
  } catch {
    localStorage.removeItem("user");
    return null;
  }
}

export function clearAuthSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

export function getCurrentAppPath() {
  if (typeof window === "undefined" || !window.location) {
    return "/";
  }

  const { pathname = "/", search = "", hash = "" } = window.location;
  return `${pathname}${search}${hash}` || "/";
}

export function isSessionTokenValid(token) {
  return Boolean(token) && !isJwtExpired(token);
}

export function getValidStoredToken({ notifyOnExpiration = false } = {}) {
  const token = getStoredToken();
  if (!isSessionTokenValid(token)) {
    if (token && notifyOnExpiration) {
      notifyInvalidSession(SESSION_EXPIRED_REASON, getCurrentAppPath(), token);
      return null;
    }
    clearAuthSession();
    return null;
  }

  return token;
}

export function persistAuthSession(user, token) {
  localStorage.setItem("token", token);

  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
    return;
  }

  localStorage.removeItem("user");
}

export function getSafeNextPath(nextPath, fallback = "/") {
  const raw = String(nextPath || "").trim();
  if (!raw) return fallback;

  if (raw.startsWith("/") && !raw.startsWith("//")) {
    return raw;
  }

  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}` || fallback;
    }
  } catch {
    // Ignore malformed redirect targets.
  }

  return fallback;
}

export function buildLoginRedirectPath(nextPath, reason = SESSION_EXPIRED_REASON) {
  const params = new URLSearchParams();
  const safeNextPath = getSafeNextPath(nextPath, "/");
  if (safeNextPath && safeNextPath !== "/") {
    params.set("next", safeNextPath);
  }
  if (reason) {
    params.set("reason", reason);
  }

  const query = params.toString();
  return query ? `/login?${query}` : "/login";
}

export function getAuthNoticeFromReason(reason) {
  if (String(reason || "").trim().toLowerCase() === SESSION_EXPIRED_REASON) {
    return "Your session expired. Please log in again to continue.";
  }

  return "";
}

export function notifyInvalidSession(
  reason = "invalid-session",
  nextPath = getCurrentAppPath(),
  expectedToken
) {
  // A response can arrive after the user has logged in again. Only invalidate
  // the session that actually authorized the failed request so an older 401
  // cannot erase a newly issued token.
  if (expectedToken !== undefined) {
    const currentToken = getStoredToken();
    if (!expectedToken || currentToken !== expectedToken) {
      return false;
    }
  }

  clearAuthSession();
  window.dispatchEvent(
    new CustomEvent(INVALID_SESSION_EVENT, {
      detail: {
        reason,
        nextPath: getSafeNextPath(nextPath, "/"),
      },
    })
  );
  return true;
}

export function handleUnauthorizedApiResponse(
  response,
  nextPath = getCurrentAppPath(),
  expectedToken
) {
  const status = Number(response?.status || 0);
  if (status === 401) {
    const invalidated = notifyInvalidSession(
      SESSION_EXPIRED_REASON,
      nextPath,
      expectedToken
    );
    throw new Error(
      invalidated
        ? "Your session expired. Please log in again to continue."
        : "That request used an older session. Please try again."
    );
  }

  return response;
}
